import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useRoutePathQuery } from '../services/routing/queries';
import type { PlannedRide, RoutePath } from '../types/routing';
import { formatSeconds } from '../utils/format';
import { decodePolyline } from '../utils/polyline';
import RoutingMap, { MapLine, MapStop } from './RoutingMap';

interface Stop {
  kind: MapStop['kind'];
  lat: number;
  lon: number;
  at: string;
  ride: PlannedRide;
}

// The driver path interleaves pooled pickups and dropoffs, so order stops by planned time.
export const timeOrderedStops = (plan: PlannedRide[]): Stop[] => {
  const stops: Stop[] = [];
  plan.forEach(ride => {
    if (ride.pickup_lat != null && ride.pickup_lon != null) {
      stops.push({
        kind: ride.pooled ? 'pooled-pickup' : 'pickup',
        lat: ride.pickup_lat,
        lon: ride.pickup_lon,
        at: ride.planned_pickup_at ?? ride.pickup_at ?? '',
        ride
      });
    }
    if (ride.dropoff_lat != null && ride.dropoff_lon != null) {
      stops.push({
        kind: 'dropoff',
        lat: ride.dropoff_lat,
        lon: ride.dropoff_lon,
        at: ride.planned_dropoff_at ?? '',
        ride
      });
    }
  });
  return stops.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
};

/**
 * What the map is actually showing. A straight-line estimate looks exactly like a road path
 * once it is drawn, so the difference has to be stated rather than implied.
 */
const PathNotice = ({
  loading,
  failed,
  path,
  drawn
}: {
  loading: boolean;
  failed: boolean;
  path: RoutePath | undefined;
  drawn: boolean;
}) => {
  if (loading) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Loading road geometry…
      </Typography>
    );
  }
  if (failed || !drawn) {
    return (
      <Alert severity="warning" sx={{ mb: 1 }}>
        No road geometry for this route — the lines below join the stops in planned order and are
        not the roads the driver would drive.
      </Alert>
    );
  }
  // Three different things, and conflating them hides an outage: the engine failed, the run
  // deliberately planned with straight lines, or it recorded no engine at all.
  const estimate = path?.service === 'manual' || path?.fallback;
  const label = path?.fallback
    ? `${path.engine ?? 'the engine'} did not answer — straight-line estimate`
    : path?.service === 'manual'
      ? `straight-line estimate (${path.engine ? 'manual engine' : 'no engine recorded'})`
      : `roads via ${path?.service}`;
  const drive = [
    path?.distance_miles == null ? null : `${path.distance_miles.toFixed(1)} mi`,
    formatSeconds(path?.duration_seconds) || null
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
      <Chip size="small" color={estimate ? 'warning' : 'success'} label={label} />
      {drive && (
        <Typography variant="caption" color="text.secondary">
          {drive} driving
        </Typography>
      )}
      {path?.truncated && (
        <Typography variant="caption" color="warning.main">
          geometry covers the first {path.geometry_stops} of {path.stops.length} stops
        </Typography>
      )}
    </Stack>
  );
};

/**
 * One planned route on a map, with what drew it. Shared by the route detail page and the run's
 * route list, so both say the same thing about geometry they did not get.
 */
export const RouteMapPanel = ({
  runId,
  routeId,
  plan,
  focusRideId = null,
  onClearFocus
}: {
  runId: string;
  routeId: string;
  plan: PlannedRide[];
  /** Show only this ride's own pickup and dropoff instead of the whole route. */
  focusRideId?: number | null;
  onClearFocus?: () => void;
}) => {
  const {
    data: path,
    isLoading,
    error
    // The engine answers for whichever leg is on screen, so focusing a ride re-asks for that
    // ride rather than reusing the route's polyline, which has no per-stop offsets to cut at.
  } = useRoutePathQuery(runId, routeId, Boolean(runId && routeId), focusRideId);

  const allStops = timeOrderedStops(plan);
  if (!allStops.length) {
    return <Typography color="text.secondary">No stops to draw — the plan is empty.</Typography>;
  }

  // Numbering stays the route's, so a focused ride keeps the position it holds in the plan.
  const numbered = allStops.map((stop, order) => ({ stop, order }));
  const focused = focusRideId != null;
  const shown = focused
    ? numbered.filter(({ stop }) => stop.ride.ride_id === focusRideId)
    : numbered;

  const mapStops: MapStop[] = shown.map(({ stop, order }) => ({
    kind: stop.kind,
    lat: stop.lat,
    lon: stop.lon,
    label: `#${order + 1} ${stop.kind.replace('-', ' ')} · ride ${stop.ride.ride_id} · ${stop.at}`
  }));
  const roadPath = decodePolyline(path?.polyline);
  // 'driver-path' whenever the geometry is real, whole route or single leg alike. Without it,
  // the fallback is the chord — drawn as the schematic it is, never as a road.
  const mapLines: MapLine[] = roadPath.length
    ? [{ kind: 'driver-path', points: roadPath }]
    : [
        {
          kind: focused ? 'ride' : 'stop-order',
          points: (focused ? shown.map(({ stop }) => stop) : allStops).map(
            stop => [stop.lat, stop.lon] as [number, number]
          )
        }
      ];

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {focused && <Chip size="small" color="info" label={`ride ${focusRideId} only`} />}
        <PathNotice
          loading={isLoading}
          failed={Boolean(error)}
          path={path}
          drawn={roadPath.length > 0}
        />
        {focused && onClearFocus && (
          <Link
            component="button"
            variant="caption"
            underline="hover"
            sx={{ mb: 1 }}
            onClick={onClearFocus}
          >
            show the whole route
          </Link>
        )}
      </Stack>
      <RoutingMap stops={mapStops} lines={mapLines} />
    </>
  );
};

export default RouteMapPanel;
